import { BACKUP_FORMAT, BACKUP_FORMAT_VERSION, specFor } from "./schema";
import type { ClinicalOSBackupFile, FormulationRef, SubstanceRef } from "./types";

export interface LegacyClinicalOSBackup {
  schema_version?: number;
  exported_at?: string;
  owner_id?: string;
  user_email?: string | null;
  tables?: Record<string, unknown>;
  counts?: Record<string, number>;
}

function rowsOf(value: unknown): Record<string, any>[] {
  return Array.isArray(value) ? value.filter((row): row is Record<string, any> => !!row && typeof row === "object" && !Array.isArray(row)) : [];
}

/** Converte backups antigos (schema_version/tables) para o envelope v1 atual, sem escrever no banco. */
export function legacyToClinicalOSBackup(raw: LegacyClinicalOSBackup): ClinicalOSBackupFile {
  const sourceTables = raw.tables ?? {};
  const ownerId = raw.owner_id ?? "legacy-import";
  const tables: Record<string, Record<string, any>[]> = {};
  const groups: ClinicalOSBackupFile["data"]["groups"] = {};

  const sourceSubstances = rowsOf(sourceTables.substances);
  const sourceFormulations = rowsOf(sourceTables.substance_formulations);

  // Backups antigos podiam conter canônicos globais por causa da política de leitura ampla.
  // Eles viram referências; nunca são reaplicados como privados.
  const substanceRefs: SubstanceRef[] = sourceSubstances.map((row) => ({
    id: String(row.id ?? ""),
    name: String(row.name ?? row.generic_name ?? "Substância"),
    canonical_substance_id: row.canonical_substance_id ?? null,
    is_global: row.is_global ?? null,
    synonyms: Array.isArray(row.synonyms) ? row.synonyms : null,
  })).filter((row) => row.id);

  const formulationRefs: FormulationRef[] = sourceFormulations.map((row) => ({
    id: String(row.id ?? ""),
    substance_id: row.substance_id ?? null,
    formulation_name: row.formulation_name ?? null,
    route: row.route ?? null,
    is_global: row.is_global ?? null,
  })).filter((row) => row.id);

  for (const [table, value] of Object.entries(sourceTables)) {
    const spec = specFor(table);
    if (!spec || spec.restoreDisabled) continue;
    let rows = rowsOf(value);
    if (table === "substances") {
      rows = rows.filter((row) => row.is_global !== true && (!row.owner_id || row.owner_id === ownerId));
    }
    if (table === "substance_formulations") {
      rows = rows.filter((row) => row.is_global !== true && (!row.owner_id || row.owner_id === ownerId));
    }
    tables[table] = rows;
    groups[table] = spec.group;
  }

  const counts: Record<string, number> = {};
  for (const [table, rows] of Object.entries(tables)) counts[table] = rows.length;

  return {
    format: BACKUP_FORMAT,
    formatVersion: BACKUP_FORMAT_VERSION,
    app: "ClinicalOS",
    appVersion: `legacy-schema-${raw.schema_version ?? "?"}`,
    exportedAt: raw.exported_at ?? new Date(0).toISOString(),
    exportedBy: ownerId,
    scope: {
      kind: "full",
      includePharmacology: true,
      includeDerived: true,
      includeAuditHistory: false,
    },
    counts,
    integrity: { algorithm: "SHA-256", payloadHash: null },
    excluded: ["segredos de integração", "credenciais de autenticação", "canônicos globais (mantidos apenas como referências)"],
    data: { tables, substanceRefs, formulationRefs, groups },
  };
}
